/**
 * frontend/src/crm/pages/PromptTemplates.jsx — modèles de prompts IA.
 * Branche v4, handoff « Modèles de prompts IA » : en-tête à sur-titre, barre de
 * pilules d'action + recherche, grille de cartes 2 colonnes, éditeur en modale
 * (aperçu du rendu + palette de variables) et confirmation de suppression.
 *
 * API inchangée (/ai/actions, /ai/templates) ; les champs gardent leurs noms de
 * dépôt (`action_type`, `system_prompt`, `user_template`). Les polices suivent
 * la convention du dépôt (font-heading / font-mono).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import api, { apiError } from "@/lib/crmApi";
import { Loader } from "@/crm/ui";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, X, Power, Search, Sparkles, Trash, ListOrdered, Wand2,
  RotateCcw, MessageSquare, BarChart3, Archive, ArchiveRestore } from "lucide-react";
import SuggestionPlaybook from "@/crm/components/copilot/SuggestionPlaybook";
import ObjectionLibrary from "@/crm/components/copilot/ObjectionLibrary";
import CopilotKpis from "@/crm/components/copilot/CopilotKpis";
import TemplateStudio from "@/crm/components/ai/TemplateStudio";
import Modal from "@/crm/components/Modal";

const EMPTY = { name: "", description: "", action_type: "APPEL", context: "",
  system_prompt: "", user_template: "", max_chars: 700, active: true };

const VAR_RE = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

// Code du filtre « Archivés » : jamais un type d'action.
const ARCHIVE = "__archive__";

export default function PromptTemplates() {
  const [meta, setMeta] = useState({ actions: [], contexts: {}, variables: [] });
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  // Deux réglages IA cohabitent ici : ce que le commercial LIT (les modèles de
  // discours) et ce que le copilote SOUFFLE pendant l'appel (la priorisation).
  // Même page parce que c'est la même décision commerciale, deux moments.
  const [view, setView] = useState("templates");

  // La liste complète est chargée une fois : les compteurs des pilules sont
  // comptés avant filtrage, et filtre + recherche s'appliquent côté client.
  const load = useCallback(async () => {
    try { const { data } = await api.get("/ai/templates?all=true"); setItems(data); }
    catch { setItems([]); }
  }, []);

  useEffect(() => { api.get("/ai/actions").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const actionLabel = useCallback((code) => meta.actions.find((a) => a.code === code)?.label || code, [meta.actions]);

  const q = query.trim().toLowerCase();
  // L'archive est un onglet à part : ailleurs, un modèle archivé n'existe
  // pas. Dans la liste, actifs d'abord, désactivés en fin (tri stable).
  const archived = filter === ARCHIVE;
  const visible = useMemo(() => (items || [])
    .filter((t) => (archived ? !!t.archived_at : !t.archived_at && (!filter || t.action_type === filter)))
    .filter((t) => !q || `${t.name || ""} ${t.user_template || ""}`.toLowerCase().includes(q))
    .sort((a, b) => (archived ? 0 : Number(!!b.active) - Number(!!a.active))),
  [items, filter, q, archived]);

  const archive = async (t) => {
    try { await api.post(`/ai/templates/${t.id}/archive`); toast.success(`« ${t.name} » archivé — restaurable depuis l'onglet Archivés`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const unarchive = async (t) => {
    try { await api.post(`/ai/templates/${t.id}/unarchive`); toast.success(`« ${t.name} » restauré`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggle = async (t) => {
    setItems((list) => list.map((x) => (x.id === t.id ? { ...x, active: !x.active } : x))); // optimiste
    try { await api.put(`/ai/templates/${t.id}`, { active: !t.active }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    load();
  };

  const restoreDefaults = async () => {
    try {
      const { data } = await api.post("/ai/templates/restore-defaults");
      const parts = [];
      if (data.created.length) parts.push(`${data.created.length} ajouté(s)`);
      if (data.updated.length) parts.push(`${data.updated.length} mis à jour`);
      if (data.kept.length) parts.push(`${data.kept.length} conservé(s) — vos modifications`);
      toast.success(`Modèles fournis : ${parts.join(" · ") || "rien à faire"}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async () => {
    const t = confirm;
    setConfirm(null);
    try { await api.delete(`/ai/templates/${t.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (items === null) return <Loader />;

  const actifs = items.filter((t) => !t.archived_at);
  const pills = [{ code: "", label: "Toutes", count: actifs.length }]
    .concat(meta.actions.map((a) => ({ code: a.code, label: a.label, count: actifs.filter((t) => t.action_type === a.code).length })))
    .concat([{ code: ARCHIVE, label: "Archivés", count: items.length - actifs.length }]);

  return (
    <div className="max-w-[1360px]">
      {/* EN-TÊTE DE PAGE */}
      <div className="flex flex-wrap items-end justify-between gap-5 mb-6">
        <div>
          <div className="font-mono text-[11px] tracking-[.14em] uppercase text-[#8a90a3] mb-2">Intelligence artificielle</div>
          <h1 className="font-heading text-[30px] font-bold tracking-[-.02em] text-[#16181d]">Modèles de prompts IA</h1>
          <p className="mt-2 text-[14.5px] text-[#61667a] max-w-[56ch] text-pretty">
            Les modèles alimentent le discours d'ouverture de « Préparer mon appel ».
            La priorisation décide de ce que le copilote souffle pendant la conversation.
            Ce que l'équipe vend — qui appelle et le catalogue — se règle dans{" "}
            <Link to="/solutions" className="font-medium text-[#4a3ae0] underline underline-offset-2 hover:no-underline">Solutions</Link>.
          </p>
        </div>
        {view === "templates" && (
          <div className="flex flex-wrap gap-2.5">
            <button data-testid="restore-defaults-btn" onClick={restoreDefaults}
              title="Réinstalle les modèles fournis. Vos modèles modifiés sont conservés."
              className="flex items-center gap-2 rounded-[11px] border border-[#e2e5ec] bg-white px-4 py-[11px] text-sm text-[#4c5164] hover:border-[#c9cedb]">
              <RotateCcw size={15} /> Modèles fournis
            </button>
            <button data-testid="new-template-btn" onClick={() => setEditing({ ...EMPTY })}
              className="flex items-center gap-2.5 bg-[#4a3ae0] text-white rounded-[11px] px-[18px] py-[11px] text-sm font-medium shadow-[0_6px_18px_rgba(74,58,224,.24)] hover:bg-[#3a2cc8]">
              <Plus size={16} /> Nouveau modèle
            </button>
          </div>
        )}
      </div>

      {/* QUATRE RÉGLAGES, QUATRE MOMENTS : ce que lit le commercial avant
          l'appel, ce que souffle le copilote pendant, ce que le terrain a
          appris (objections), et ce que les chiffres en disent (mesure). */}
      <div className="flex flex-wrap gap-1 mb-[18px]">
        {[["templates", "Modèles de discours", Wand2],
          ["playbook", "Priorisation des suggestions", ListOrdered],
          ["objections", "Bibliothèque d'objections", MessageSquare],
          ["mesure", "Mesure", BarChart3]].map(([key, label, Icon]) => {
          const on = view === key;
          return (
            <button key={key} data-testid={`ia-view-${key}`} onClick={() => setView(key)}
              className={`flex items-center gap-2 rounded-xl px-3.5 py-2 text-[13.5px] font-medium ${on ? "bg-[#16181d] text-white" : "text-[#4c5164] hover:bg-[#f1f2f7]"}`}>
              <Icon size={15} className={on ? "text-white" : "text-[#9aa0b2]"} /> {label}
            </button>
          );
        })}
      </div>

      {view === "playbook" && <SuggestionPlaybook />}
      {view === "objections" && <ObjectionLibrary />}
      {view === "mesure" && <CopilotKpis />}

      {view === "templates" && (<>
      {/* FILTRES */}
      <div className="flex flex-wrap items-center gap-2.5 mb-[18px]">
        {pills.map((p) => {
          const on = filter === p.code;
          const count = p.count;
          return (
            <button key={p.code || "all"} data-testid={p.code === ARCHIVE ? "tpl-filter-archive" : undefined} onClick={() => setFilter(p.code)}
              className={`flex items-center gap-2 border rounded-[10px] px-[13px] py-2 text-[13.5px] font-medium ${on ? "bg-[#eeecff] border-[#b9b1ff] text-[#3a2cc8]" : "bg-white border-[#e2e5ec] text-[#4c5164] hover:border-[#c9cedb]"}`}>
              <span>{p.label}</span>
              <span className="font-mono text-[11.5px] opacity-[.72]">{count}</span>
            </button>
          );
        })}
        <div className="flex-1" />
        <div className="flex items-center gap-2.5 min-w-[220px] bg-white border border-[#e2e5ec] rounded-[11px] px-3 py-2.5 focus-within:border-[#5b4bff] focus-within:shadow-[0_0_0_3px_rgba(91,75,255,.14)]">
          <Search size={15} className="text-[#9aa0b2] shrink-0" />
          <input data-testid="tpl-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Filtrer les modèles…" className="w-full border-0 outline-none bg-transparent text-[13.5px]" />
        </div>
      </div>

      {/* GRILLE */}
      {visible.length === 0 ? (
        <div className="bg-white border border-[#e6e8ee] rounded-2xl px-6 py-[54px] text-center">
          <div className="h-11 w-11 mx-auto mb-3.5 rounded-xl bg-[#eeecff] flex items-center justify-center">
            <Sparkles size={20} className="text-[#4a3ae0]" />
          </div>
          <div className="font-heading text-base font-bold">Aucun modèle</div>
          <p className="mt-[7px] text-[13.5px] text-[#71768a]">{archived ? "Aucun modèle archivé." : "Aucun modèle ne correspond à ce filtre."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 min-[1181px]:grid-cols-2 gap-[14px]">
          {visible.map((t) => {
            const vars = [...new Set((t.user_template || "").match(VAR_RE) || [])].slice(0, 4).map((v) => v.replace(/[{}]/g, "").trim());
            return (
              <div key={t.id} data-testid="template-card"
                className={`bg-white border border-[#e6e8ee] rounded-2xl p-[18px] flex flex-col gap-[11px] ${t.active ? "" : "opacity-[.62]"}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2.5">
                      <span className="font-heading text-[15.5px] font-bold">{t.name}</span>
                      {t.archived_at ? (
                        <span className="font-mono text-[10.5px] tracking-[.06em] uppercase text-[#8a90a3] bg-[#f1f2f7] rounded-full px-2 py-[3px]"
                          title={`Archivé le ${new Date(t.archived_at).toLocaleDateString("fr-FR")}${t.archived_by ? ` par ${t.archived_by}` : ""}`}>archivé</span>
                      ) : !t.active && (
                        <span className="font-mono text-[10.5px] tracking-[.06em] uppercase text-[#8a90a3] bg-[#f1f2f7] rounded-full px-2 py-[3px]">inactif</span>
                      )}
                    </div>
                    <div className="text-[12.5px] text-[#8a90a3] mt-1">
                      {[actionLabel(t.action_type), t.context, `v${t.version}`, t.author,
                        t.max_chars ? `${t.max_chars} car. max` : null].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {t.archived_at ? (
                      <>
                        {/* Depuis l'archive seulement : restaurer, ou supprimer
                            pour de bon — avec confirmation. */}
                        <button data-testid="unarchive-template-btn" onClick={() => unarchive(t)} title="Restaurer"
                          className="p-1.5 rounded-[9px] text-[#7c8296] hover:bg-[#f1f2f7] hover:text-[#2d3142]"><ArchiveRestore size={16} /></button>
                        <button data-testid="delete-template-btn" onClick={() => setConfirm(t)} title="Supprimer définitivement"
                          className="p-1.5 rounded-[9px] text-[#b3b8c7] hover:bg-[#fdeceb] hover:text-[#d64a3a]"><Trash2 size={16} /></button>
                      </>
                    ) : (
                      <>
                        <button data-testid="toggle-template-btn" onClick={() => toggle(t)} title={t.active ? "Désactiver" : "Activer"}
                          className={`p-1.5 rounded-[9px] hover:bg-[#f1f2f7] ${t.active ? "text-[#2f9e5c]" : "text-[#c9cedb]"}`}><Power size={16} /></button>
                        <button data-testid="edit-template-btn" onClick={() => setEditing(t)} title="Modifier"
                          className="p-1.5 rounded-[9px] text-[#7c8296] hover:bg-[#f1f2f7] hover:text-[#2d3142]"><Pencil size={16} /></button>
                        {/* Pas de suppression directe : on archive, réversible,
                            et l'archive seule permet de supprimer. */}
                        <button data-testid="archive-template-btn" onClick={() => archive(t)} title="Archiver"
                          className="p-1.5 rounded-[9px] text-[#b3b8c7] hover:bg-[#f1f2f7] hover:text-[#2d3142]"><Archive size={16} /></button>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-[13.5px] text-[#61667a] text-pretty">{t.description || "Aucune description."}</p>
                <div className="font-mono text-xs leading-[1.6] text-[#5b6072] bg-[#f7f8fa] border border-[#eceef4] rounded-[11px] px-3 py-[11px]">
                  {(t.user_template || "").length > 150 ? `${t.user_template.slice(0, 150)}…` : t.user_template}
                </div>
                {vars.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    {vars.map((v) => (
                      <span key={v} className="font-mono text-[11px] text-[#4a3ae0] bg-[#eeecff] rounded-md px-[7px] py-[3px]">{v}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      </>)}

      {/* Enregistrer rafraîchit la liste derrière l'atelier sans le fermer :
          on ferme avec Fermer / Échap, quand on a fini. */}
      {editing && (
        <TemplateStudio template={editing} meta={meta} onClose={() => setEditing(null)}
          onSaved={() => load()} />
      )}
      {confirm && <ConfirmDelete name={confirm.name} onClose={() => setConfirm(null)} onConfirm={remove} />}
    </div>
  );
}

function ConfirmDelete({ name, onClose, onConfirm }) {
  return (
    <Modal onClose={onClose} zClassName="z-[70]" testid="template-delete-modal" label="Supprimer le modèle"
      className="max-w-[440px] rounded-[18px] shadow-[0_28px_70px_rgba(10,12,24,.34)]">
      <div className="flex items-start gap-3.5">
        <div className="h-10 w-10 shrink-0 rounded-[11px] bg-[#fdeceb] flex items-center justify-center">
          <Trash size={19} className="text-[#d64a3a]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-[17.5px] font-bold">Supprimer définitivement ?</h3>
          <p className="mt-2 text-sm text-[#61667a] text-pretty">
            « {name} » disparaîtra de l'archive, sans retour possible. Les discours déjà générés sont conservés.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-2.5 mt-[22px]">
        <button onClick={onClose} className="border border-[#e2e5ec] bg-white rounded-[11px] px-4 py-[11px] text-sm text-[#4c5164] hover:border-[#c9cedb]">Annuler</button>
        <button data-testid="template-delete-confirm" onClick={onConfirm}
          className="bg-[#d64a3a] text-white rounded-[11px] px-[18px] py-[11px] text-sm font-medium hover:bg-[#bf3c2d]">Supprimer</button>
      </div>
    </Modal>
  );
}
